import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  LayoutDashboard,
  Receipt,
  UtensilsCrossed,
  Package,
  Table,
  BarChart3,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  UserCircle,
  Tag,
} from 'lucide-react';
import { theme, media } from '../../styles/theme';
import logoImage from '../assets/battasesukhsagarlogo.png';

const SidebarContainer = styled.aside`
  width: 260px;
  height: 100vh;
  background: linear-gradient(180deg, ${theme.colors.white} 0%, ${theme.colors.secondary} 100%);
  box-shadow: ${theme.shadows.xl};
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  z-index: ${theme.zIndex.fixed};
  transition: transform ${theme.transitions.duration.slow} ${theme.transitions.easing.easeOut};
  border-right: 1px solid ${theme.colors.border};

  ${media.down.lg} {
    transform: ${({ isOpen }) => isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    box-shadow: ${({ isOpen }) => isOpen ? theme.shadows['2xl'] : theme.shadows.xl};
  }
`;

const Overlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: ${theme.zIndex.modalBackdrop};
  transition: opacity ${theme.transitions.duration.base} ${theme.transitions.easing.easeOut};
  animation: fadeIn 0.3s ease;
  pointer-events: none;

  ${media.down.lg} {
    display: ${({ isOpen }) => isOpen ? 'block' : 'none'};
    opacity: ${({ isOpen }) => isOpen ? 1 : 0};
    pointer-events: ${({ isOpen }) => isOpen ? 'auto' : 'none'};
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  position: fixed;
  top: ${theme.spacing.md};
  left: ${theme.spacing.md};
  z-index: ${theme.zIndex.fixed + 1};
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.lg};
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%);
  color: ${theme.colors.white};
  border: none;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  box-shadow: ${theme.shadows.lg};
  transition: all ${theme.transitions.duration.base} ${theme.transitions.easing.easeOut};

  ${media.down.lg} {
    display: flex;
  }

  &:hover {
    transform: scale(1.05);
    box-shadow: ${theme.shadows.xl};
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const CloseButton = styled.button`
  display: none;
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.text};
  border: none;
  cursor: pointer;
  align-items: center;
  justify-content: center;

  @media (max-width: ${theme.breakpoints.lg}) {
    display: flex;
  }

  &:hover {
    background-color: ${theme.colors.status.outOfStock};
    color: ${theme.colors.white};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Logo = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.xl};
  border-bottom: 2px solid ${theme.colors.border};
  position: relative;
  background: linear-gradient(135deg, ${theme.colors.primary}05 0%, ${theme.colors.accentTeal}05 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  
  .logo-image {
    width: 100%;
    max-width: 120px;
    height: auto;
    object-fit: contain;
    flex-shrink: 0;
  }

  ${media.down.sm} {
    padding: ${theme.spacing.xs} ${theme.spacing.md};
    
    .logo-image {
      max-width: 90px;
    }
  }
`;

const NavList = styled.nav`
  flex: 1;
  padding: ${theme.spacing.md};
  overflow-y: auto;
`;

const NavItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.lg};
  background: ${({ active }) => active 
    ? `linear-gradient(135deg, ${theme.colors.primary}15 0%, ${theme.colors.accentTeal}10 100%)` 
    : 'transparent'};
  color: ${({ active }) => active ? theme.colors.primary : theme.colors.text};
  font-weight: ${({ active }) => active ? theme.typography.fontWeight.semiBold : theme.typography.fontWeight.regular};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily};
  border: none;
  cursor: pointer;
  transition: all ${theme.transitions.duration.base} ${theme.transitions.easing.easeOut};
  text-align: left;
  min-height: 44px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, ${theme.colors.primary} 0%, ${theme.colors.accentTeal} 100%);
    transform: ${({ active }) => active ? 'scaleY(1)' : 'scaleY(0)'};
    transition: transform ${theme.transitions.duration.base} ${theme.transitions.easing.easeOut};
    border-radius: 0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0;
  }

  &:hover {
    background: ${({ active }) => active 
      ? `linear-gradient(135deg, ${theme.colors.primary}20 0%, ${theme.colors.accentTeal}15 100%)` 
      : `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.secondaryDark} 100%)`};
    transform: translateX(4px);
    box-shadow: ${theme.shadows.sm};
  }

  svg {
    width: 20px;
    height: 20px;
    transition: transform ${theme.transitions.duration.base};
  }

  &:hover svg {
    transform: scale(1.1);
  }

  ${media.down.sm} {
    font-size: ${theme.typography.fontSize.sm};
    padding: ${theme.spacing.sm} ${theme.spacing.md};
  }
`;

const LogoutButton = styled(NavItem)`
  margin-top: ${theme.spacing.md};
  border-top: 2px solid ${theme.colors.border};
  padding-top: ${theme.spacing.md};
  color: ${theme.colors.error};

  &::before {
    background: linear-gradient(180deg, ${theme.colors.error} 0%, ${theme.colors.accent} 100%);
  }

  &:hover {
    background: linear-gradient(135deg, ${theme.colors.error}15 0%, ${theme.colors.accent}10 100%);
    color: ${theme.colors.error};
  }
`;

const RupeesIcon = ({ size = 20, className }) => (
  <span 
    style={{ 
      fontSize: `${size}px`, 
      fontWeight: theme.typography.fontWeight.bold,
      fontFamily: 'Arial, sans-serif',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: `${size}px`,
      height: `${size}px`
    }}
    className={className}
  >
    ₹
  </span>
);

const menuItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/orders', label: 'Orders', icon: Receipt },
  { path: '/admin/menu', label: 'Menu', icon: UtensilsCrossed },
  { path: '/admin/inventory', label: 'Inventory', icon: Package },
  { path: '/admin/tables', label: 'Tables', icon: Table },
  { path: '/admin/customers', label: 'Customers', icon: UserCircle },
  { path: '/admin/financial', label: 'Financial', icon: RupeesIcon },
  { path: '/admin/promotions', label: 'Promotions', icon: Tag },
  { path: '/admin/reports', label: 'Reports', icon: BarChart3 },
  { path: '/admin/staff', label: 'Staff', icon: Users },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
];

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    navigate('/login');
  };

  const handleNavClick = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  return (
    <>
      <MobileMenuButton onClick={() => setIsOpen(true)}>
        <Menu />
      </MobileMenuButton>
      <Overlay isOpen={isOpen} onClick={() => setIsOpen(false)} />
      <SidebarContainer isOpen={isOpen}>
        <Logo>
          <img src={logoImage} alt="Logo" className="logo-image" />
          <CloseButton onClick={() => setIsOpen(false)}>
            <X />
          </CloseButton>
        </Logo>
        <NavList>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <NavItem
                key={item.path}
                active={isActive}
                onClick={() => handleNavClick(item.path)}
              >
                <Icon />
                <span>{item.label}</span>
              </NavItem>
            );
          })}
          <LogoutButton onClick={handleLogout}>
            <LogOut />
            <span>Logout</span>
          </LogoutButton>
        </NavList>
      </SidebarContainer>
    </>
  );
}

export default Sidebar;
